import { api } from "./api";

export interface ClientResponse {
  id: string;
  tenantId: string;
  nome: string;
  cpf: string;
  telefone: string;
  whatsapp: string | null;
  dataNascimento: string | null;
  rg: string | null;
  nacionalidade: string | null;
  estadoCivil: string | null;
  profissao: string | null;
  cep: string | null;
  endereco: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  chavePix: string | null;
  tipoChavePix: string | null;
  contatosEmergencia: { nome: string; telefone: string; relacao?: string }[] | null;
  /**
   * Snapshot completo das respostas do onboarding. Inclui campos custom
   * (conditional_input, multi-field, video URIs) que nao tem coluna propria.
   */
  dadosOnboarding: Record<string, unknown> | null;
  serasa: boolean;
  ativo: boolean;
  /**
   * Status derivado do loan_pipeline mais recente. Preenchido apenas na
   * lista (findAll). Ver Client entity no backend pra semantica.
   */
  pipelineStatus?: string | null;
  /** Cliente bloqueado no tenant — nao consegue solicitar novo emprestimo. */
  bloqueado?: boolean;
  bloqueadoAt?: string | null;
  bloqueadoMotivo?: string | null;
  createdAt: string;
}

export interface CreateClientData {
  nome: string;
  cpf: string;
  telefone: string;
  whatsapp?: string | null;
  dataNascimento?: string;
  rg?: string;
  nacionalidade?: string;
  estadoCivil?: string;
  profissao?: string;
  cep?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  chavePix?: string;
  tipoChavePix?: string;
  contatosEmergencia?: { nome: string; telefone: string; relacao?: string }[];
  serasa?: boolean;
}

export interface UpdateClientData extends Partial<CreateClientData> {
  ativo?: boolean;
}

interface PaginatedClients {
  data: ClientResponse[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export async function listClients(params: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<PaginatedClients> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.limit) query.set("limit", String(params.limit));
  if (params.search) query.set("search", params.search);

  const response = await api.get<ClientResponse[]>(`/clients?${query.toString()}`);
  return {
    data: response.data || [],
    meta: response.meta || { page: 1, limit: 10, total: 0, totalPages: 0 },
  };
}

export async function getClient(id: string): Promise<ClientResponse> {
  const response = await api.get<ClientResponse>(`/clients/${id}`);
  return response.data!;
}

export async function createClient(data: CreateClientData): Promise<ClientResponse> {
  const response = await api.post<ClientResponse>("/clients", data);
  return response.data!;
}

export async function updateClient(id: string, data: UpdateClientData): Promise<ClientResponse> {
  const response = await api.put<ClientResponse>(`/clients/${id}`, data);
  return response.data!;
}

export async function deleteClient(id: string): Promise<void> {
  await api.delete(`/clients/${id}`);
}

export async function blockClient(id: string, motivo?: string): Promise<void> {
  await api.post(`/clients/${id}/block`, motivo?.trim() ? { motivo: motivo.trim() } : {});
}

export async function unblockClient(id: string): Promise<void> {
  await api.post(`/clients/${id}/unblock`, {});
}

export interface ScoreHistorico {
  operacoes: number;
  diariasPagas: number;
  diariasAtraso: number;
  maiorAtraso: number;
  renegociacoes: number;
  liquidadas: number;
  empresas: number;
}

export interface ClientScoreResponse {
  score: number;
  pontualidade: number | null;
  historico: ScoreHistorico;
}

export async function getClientScore(id: string): Promise<ClientScoreResponse> {
  const response = await api.get<ClientScoreResponse>(`/clients/${id}/score`);
  return response.data!;
}

export async function getClientScoresBatch(
  clienteIds: string[],
): Promise<Record<string, number>> {
  if (clienteIds.length === 0) return {};
  const response = await api.post<Record<string, number>>("/clients/scores", { clienteIds });
  return response.data ?? {};
}
